import { useTranslation } from 'react-i18next';
import { Badge, Button, Icon, Panel } from '../../ui/index.ts';
import { FlipCard } from '../fx/FlipCard.tsx';
import { Stamp } from '../fx/Stamp.tsx';
import { RoleFigure } from '../figures/RoleFigure.tsx';
import { useGame } from '../GameContext.tsx';
import { TeamList } from '../hud/TeamList.tsx';
import styles from './Briefing.module.css';

/** GDD §5.1: the roles are dealt face down and turn over; only the infiltrator sees that they are one. */
export function Briefing() {
  const { t } = useTranslation('game');
  const { view, send, g } = useGame();
  const role = view.you.role;
  const ready = view.briefing?.ready.includes(view.you.id) ?? false;
  const seat = view.players.find((p) => p.id === view.you.id)?.seat ?? 0;

  return (
    <div className={styles['grid']}>
      <FlipCard delay={0.1}>
        <Panel tone="lamp" className={styles['role']} title={t('briefing.yourRole')}>
          <div className={styles['roleHead']}>
            <RoleFigure role={role} seat={seat} size={112} className={styles['figure']} />
            <p className={styles['roleName']}>{g(`roles.${role}`)}</p>
          </div>
          <dl className={styles['facts']}>
            <dt>{t('briefing.sees')}</dt>
            <dd>{g(`roleInfo.${role}.sees`)}</dd>
            <dt>{t('briefing.does')}</dt>
            <dd>{g(`roleInfo.${role}.does`)}</dd>
            <dt>{t('briefing.powers')}</dt>
            <dd>{g(`roleInfo.${role}.powers`)}</dd>
          </dl>
        </Panel>
      </FlipCard>

      <FlipCard delay={0.45}>
        <Panel
          tone="night"
          className={[styles['card'], view.you.isInfiltrator ? styles['stamped'] : ''].join(' ')}
          title={t('briefing.secret')}
        >
          {view.you.isInfiltrator ? (
            <>
              <Stamp className={styles['stamp']} delay={1.25} sound>
                {t('briefing.stamp')}
              </Stamp>
              <p>{t('briefing.infiltratorBody')}</p>
            </>
          ) : (
            <>
              <p className={styles['faceDown']} aria-hidden="true">
                {'?'}
              </p>
              <p>{t('briefing.coverBody')}</p>
            </>
          )}
        </Panel>
      </FlipCard>

      <Panel tone="blueprint" className={styles['target']} title={t('briefing.target')}>
        <p className={styles['mapName']}>{g(`maps.${view.game.mapId}.name`)}</p>
        <p>{g(`maps.${view.game.mapId}.place`)}</p>
        <p className={styles['special']}>{g(`maps.${view.game.mapId}.special`)}</p>
        <div className={styles['chips']}>
          <Badge tone="amber">{t('briefing.loot', { amount: view.game.baseLoot })}</Badge>
          <Badge tone="cyan">{g(`difficulty.${view.game.difficulty}`)}</Badge>
          <Badge>{t('briefing.players', { count: view.game.playerCount })}</Badge>
        </div>
      </Panel>

      <Panel tone="night" className={styles['team']} title={t('briefing.team')}>
        <TeamList />
        <div className={styles['go']}>
          <Button
            size="lg"
            icon={ready ? 'check' : 'play'}
            disabled={ready}
            onClick={() => send({ type: 'ready' })}
          >
            {ready ? t('briefing.waiting') : t('briefing.ready')}
          </Button>
          <p className={styles['note']}>
            <Icon name="clock" size="1em" /> {t('briefing.skip')}
          </p>
        </div>
      </Panel>
    </div>
  );
}
